import { useCallback, useContext, useEffect, useMemo } from 'react';

import { mergeSx, SxProps, Box } from '@cast/design-system';

import { getUser } from '@cast/infrastructure-auth';
import { useIsClusterDemoMode } from '@cast/infrastructure-demo';
import { useTourV2 } from 'core/tour-v2/TourProvider';

import { Beacon, TourFooterInitiate, TourTooltip } from './_components';
import { GlobalTourStepProps, TourContext } from './providers';
import { wasDismissed } from './utils';

export type InitiateTourProps = {
  type: string;
  steps: string[];
  autoInit?: boolean;
  globalTourStepProps?: Record<string, GlobalTourStepProps>;
  endTourOnDestroy?: boolean;
  forceVisible?: boolean;
  message?: string;
  dismissedMessage?: string;
  sx?: SxProps;
};

export const InitiateTour = ({
  type,
  steps,
  autoInit,
  globalTourStepProps,
  endTourOnDestroy = true,
  forceVisible = false,
  message = 'Take the tour to learn more about this page. Are you ready?',
  dismissedMessage = 'Go back to the tour to learn more by clicking on the bubble.',
  sx,
}: InitiateTourProps) => {
  const { isTourActive: isTourV2Active } = useTourV2();
  const {
    isActive,
    isInitActive,
    setIsInitActive,
    isDismissedOnce,
    finishTour,
    finishTourImmediately,
  } = useContext(TourContext);
  const isClusterDemoMode = useIsClusterDemoMode();

  const showTour = forceVisible || isClusterDemoMode;
  const storageKey = `dismissed-tour-${type}-${getUser()?.id}`;

  const wasDismissedBefore = useMemo(() => {
    return wasDismissed(type);
  }, [type]);

  if (wasDismissedBefore) {
    autoInit = false;
  }

  const openInitTooltip = useCallback(() => {
    setIsInitActive(true);
  }, [setIsInitActive]);

  useEffect(() => {
    if (autoInit && !isInitActive && !isActive) {
      const timeoutId = setTimeout(openInitTooltip, 1200);

      return () => {
        clearTimeout(timeoutId);
      };
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoInit, storageKey]);

  useEffect(() => {
    return () => {
      if (endTourOnDestroy) {
        finishTour();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickAway = useCallback(() => {
    if (isDismissedOnce) {
      finishTourImmediately();
    }
  }, [isDismissedOnce, finishTourImmediately]);

  if (isActive || !showTour || isTourV2Active) {
    return null;
  }

  return (
    <TourTooltip
      open={isInitActive}
      text={isDismissedOnce ? dismissedMessage : message}
      footer={
        !isDismissedOnce ? (
          <TourFooterInitiate
            type={type}
            steps={steps}
            globalTourStepProps={globalTourStepProps}
          />
        ) : null
      }
      placement="bottom-start"
      popperProps={{
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [-10, 0],
            },
          },
        ],
      }}
      onClickAway={handleClickAway}
      testId="initiate-tour"
    >
      <Box sx={mergeSx({ height: 'max-content' }, sx)}>
        <Beacon
          init
          isActive={isInitActive}
          onClick={() => {
            openInitTooltip();
          }}
          testId="initiate-tour-beacon"
        />
      </Box>
    </TourTooltip>
  );
};
